// Funil de engajamento do dia (seção Engajamento): Enviados →
// Entregues → Lidos, 3 KPIs conectados visualmente por setas.
// Valores absolutos vêm do bloco `funnel`; taxas e deltas (vs dia
// anterior) vêm dos kpis. Cada etapa é clicável → Consulta
// pré-filtrada no dia selecionado.
import {
  ChevronRightIcon,
  MailCheckIcon,
  MailOpenIcon,
  SendIcon,
} from "lucide-react"
import { Fragment } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import type {
  NotificationOverviewFunnelType,
  NotificationOverviewKpisType,
} from "@/types/notification-center"

import {
  formatDashboardInteger,
  formatDashboardRate,
  type NotificationSearchLinkFilters,
} from "../dashboard-utils"
import { DashboardKpiCard } from "./dashboard-kpi-card"

interface DashboardFunnelProps {
  funnel?: NotificationOverviewFunnelType
  kpis?: NotificationOverviewKpisType
  isLoading: boolean
  hasError: boolean
  onRetry: () => void
  // Janela selecionada resolvida (startDate/endDate) — deep-links.
  range: { startDate?: string; endDate?: string }
  // Rótulo do delta (vs dia/período anterior) — vem do container.
  deltaLabel: string
}

export const DashboardFunnel = ({
  funnel,
  kpis,
  isLoading,
  hasError,
  onRetry,
  range,
  deltaLabel,
}: DashboardFunnelProps) => {
  const { t, i18n } = useTranslation()
  const dashboardPrefix =
    "pages.notificationCenter.dashboardPage"
  const funnelPrefix = `${dashboardPrefix}.funnel`

  if (isLoading) {
    return (
      <div
        data-testid="dashboard-funnel-skeleton"
        className="grid grid-cols-3 gap-2"
      >
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton
            key={`dashboard-funnel-skeleton-${index}`}
            className="h-28 w-full"
          />
        ))}
      </div>
    )
  }
  if (hasError || !funnel || !kpis) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-md border border-red-200 bg-red-50 p-6">
        <p className="text-center text-sm text-red-700">
          {t(`${dashboardPrefix}.loadError`)}
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={onRetry}
        >
          {t(`${dashboardPrefix}.retry`)}
        </Button>
      </div>
    )
  }

  // Etapas do funil: valor absoluto + taxa na sub-linha (entrega/
  // leitura) + delta vs dia anterior da métrica correspondente.
  const steps = [
    {
      key: "sent" as const,
      icon: SendIcon,
      tone: "primary" as const,
      value: funnel.sent,
      subline: undefined,
      delta: kpis.sent.delta,
      filters: { ...range },
    },
    {
      key: "delivered" as const,
      icon: MailCheckIcon,
      tone: "success" as const,
      value: funnel.delivered,
      subline: `${t(`${funnelPrefix}.deliveryRate`)}: ${formatDashboardRate(
        kpis.deliveryRate.value,
        i18n.language
      )}`,
      delta: kpis.deliveryRate.delta,
      filters: {
        status: "DELIVERED",
        ...range,
      } as NotificationSearchLinkFilters,
    },
    {
      key: "read" as const,
      icon: MailOpenIcon,
      tone: "info" as const,
      value: funnel.read,
      subline: `${t(`${funnelPrefix}.readRate`)}: ${formatDashboardRate(
        kpis.readRate.value,
        i18n.language
      )}`,
      delta: kpis.readRate.delta,
      filters: {
        status: "READ",
        ...range,
      } as NotificationSearchLinkFilters,
    },
  ]

  return (
    <div className="flex flex-row items-center gap-1.5 @3xl:gap-2">
      {steps.map((step, index) => (
        <Fragment key={step.key}>
          {index > 0 && (
            <ChevronRightIcon
              aria-hidden="true"
              className="size-4 shrink-0 self-center text-muted-foreground @3xl:size-5"
            />
          )}
          <div className="min-w-0 flex-1">
            <DashboardKpiCard
              icon={step.icon}
              tone={step.tone}
              label={t(`${funnelPrefix}.${step.key}`)}
              value={formatDashboardInteger(
                step.value,
                i18n.language
              )}
              subline={step.subline}
              delta={step.delta}
              deltaLabel={deltaLabel}
              filters={step.filters}
              ariaLabel={t(
                `${funnelPrefix}.links.${step.key}`
              )}
            />
          </div>
        </Fragment>
      ))}
    </div>
  )
}
